import React from 'react';
import PropTypes from 'prop-types';
import { SearchPage } from '../components/Views/SearchPage';
import {
  SearchItemsTitleService,
  fromUrlToObj,
  getSeoData,
  adaptFiltersForState,
  getLocaleFromBrowser,
  SearchItemsH1Service
} from '../utils/helpers';
import { getI18nForLang } from '../utils/helpers/getI18nForLang';
import CONFIG from '../config';
import Layout from '../components/Layout/Layout';
import {
  setCategoryIds,
  setChosenLocationNames,
  setCurrentState,
  setSearchSort,
  setCurrentCity,
  setLocationIds
} from '../actions';
import { Cities, BUILDING_TYPE_ID } from '../utils/helpers/constants';
import { getStaticData } from '../utils/helpers/getStaticData';
import {
  getDataForTransformationsQuery,
  searchTotalCountQuery
} from '../api/queries';
import { ItemConnectionSort } from '../graphql/types';
import { getBreadcrumbs } from '../utils/helpers/getBreadcrumbs';

const Search = ({ transformedObj, totalAdsCount, h1, ...rest }) => {
  return (
    <SearchPage
      transformedObj={transformedObj}
      totalAdsCount={totalAdsCount}
      h1={h1}
      {...rest}
    />
  );
};

Search.getInitialProps = async ({
  apolloClient,
  asPath,
  query,
  res,
  req,
  store,
  lng: ctxLng
}) => {
  let currentLang;
  if (typeof window !== 'undefined') {
    currentLang = getLocaleFromBrowser();
  } else {
    currentLang = ctxLng || CONFIG.defaultLocale;
  }
  currentLang = currentLang || CONFIG.defaultLocale;

  const [dataForTransform, seoData] = await Promise.all([
    apolloClient.query({ query: getDataForTransformationsQuery }),
    getSeoData(apolloClient, asPath, req),
    getStaticData(apolloClient, true)
  ]);

  const transformedObj = fromUrlToObj(dataForTransform, query);

  const { breadcrumbs } = getBreadcrumbs(
    dataForTransform?.data,
    transformedObj?.filter || {},
    'search-result'
  );

  if (res && transformedObj.noMatch) {
    res.statusCode = 404;

    return {
      statusCode: 404
    };
  }
  const locationsIds = transformedObj?.filter?.locationIds || [];
  const storedFormState = store.getState().searchForm.currentState;
  const objForStore = adaptFiltersForState(transformedObj?.filter);
  const cityId =
    objForStore?.city_id || storedFormState?.city_id || Cities.Baku.id;
  store.dispatch(
    setCurrentState({
      ...storedFormState,
      ...objForStore,
      city_id: cityId,
      category_id:
        objForStore?.category_id ||
        storedFormState?.category_id ||
        BUILDING_TYPE_ID.FLAT_ALL,
      sorting: transformedObj?.sorting
    })
  );

  store.dispatch(setCategoryIds([objForStore.category_id]));
  store.dispatch(setCurrentCity(cityId));
  // eslint-disable-next-line
  const { category_touched, ...filtersWithoutCategoryTouched } =
    transformedObj.filter;
  transformedObj.filter = filtersWithoutCategoryTouched;
  const totalAdsCountData = await apolloClient.query({
    query: searchTotalCountQuery,
    variables: {
      filter: filtersWithoutCategoryTouched
    }
  });
  store.dispatch(
    setSearchSort(transformedObj.sorting || ItemConnectionSort.BumpedAtDesc)
  );

  store.dispatch(setLocationIds(locationsIds));

  store.dispatch(
    setChosenLocationNames(
      dataForTransform.data.locations
        .filter(location => locationsIds.includes(location.id))
        .map(location => location.name.trim())
    )
  );

  if (!seoData.title || !seoData?.description) {
    // Use thread-safe i18n instance with specific language instead of singleton
    // This prevents race conditions when multiple requests run concurrently
    // Each request gets its own i18n instance with the correct language
    const threadSafeI18n = getI18nForLang(currentLang);

    const rawTitle = new SearchItemsTitleService(
      dataForTransform.data,
      transformedObj.filter,
      threadSafeI18n
    ).getTitle();

    seoData.title = `${rawTitle} - bina.az`;

    if (!seoData.h1) {
      seoData.h1 = new SearchItemsH1Service(
        dataForTransform.data,
        transformedObj.filter,
        threadSafeI18n
      )
        .getH1()
        .trim();
    }

    seoData.description = threadSafeI18n.t('default_search_description', {
      title: rawTitle
    });
  }

  return {
    transformedObj,
    seoData,
    totalAdsCount: totalAdsCountData?.data?.itemsConnection?.totalCount,
    h1: seoData.h1,
    isSearchPage: true,
    breadcrumbs,
    currentLang
  };
};

Search.propTypes = {
  transformedObj: PropTypes.object,
  breadcrumbs: PropTypes.array,
  totalAdsCount: PropTypes.number,
  h1: PropTypes.string,
  citiesUnPopularList: PropTypes.array,
  regions: PropTypes.object,
  categories: PropTypes.array
};

Search.getLayout = (page, pageProps) => (
  <Layout isBackgroundBanner isBanner pageProps={pageProps}>
    {page}
  </Layout>
);

export default Search;
